import { useCallback, useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Page, Layout, Card, BlockStack, InlineStack, Text, IndexTable, useBreakpoints, Pagination, Box, Spinner, Banner } from '@shopify/polaris';
import { cycleCountService } from '#client/services/cycleCount/cycleCountService';
import ActivityLog from '#client/components/ActivityLog';
import { ITEMS_PER_PAGE, PERFORM_STATUSES, STATUS } from '#client/pages/CycleCounts/constants/cycleCount';
import { hasPermissionForLocation } from '#client/utils/helpers';
import RejectModal from './components/RejectModal';
import styles from './CycleCountDetail.module.css';
import CycleCountBreadcrumbs from '../Perform/CycleCountBreadcrumbs';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { SessionContext } from '#client/contexts/SessionContext';
import NoPermissions from '#client/pages/NoPermissions';
import CycleCountDetails from '../components/CycleCountDetails';

// Column configuration
const COLUMN_FIELDS = ['productName', 'sku', 'variant', 'startingQuantity', 'countedQuantity', 'discrepancy'];

const TABLE_HEADINGS = [
    { title: 'Product Name' },
    { title: 'SKU' },
    { title: 'Variant' },
    { title: 'Expected Qty', alignment: 'end' },
    { title: 'Counted Qty', alignment: 'end' },
    { title: 'Discrepancy', alignment: 'end' },
];

// Utility functions
const getDiscrepancyBackground = (discrepancy) => {
    if (discrepancy > 0) return '#CDFEE1';
    if (discrepancy < 0) return '#FEE9E8';
    return 'transparent';
};

const transformItemsToProductData = (items) => {
    return items.map((item, index) => ({
        id: `product-${index}-${item.sku}`,
        sku: item.sku,
        productName: item.product?.title || 'Unknown Product',
        variant: item.title || '',
        startingQuantity: item.startingQuantity,
        countedQuantity: item.countedQuantity,
        discrepancy: item.countedQuantity - item.startingQuantity,
        isCounted: item.isCounted,
    }));
};

// Sub-components
const ProductTableRow = ({ product }) => (
    <IndexTable.Row id={product.id} key={product.id} position={product.id} style={!product.isCounted ? { backgroundColor: '#FEE9E8' } : undefined}>
        <IndexTable.Cell className={styles.productNameColumn}>
            <span className={styles.textEllipsis} title={product.productName}>
                {product.productName}
            </span>
        </IndexTable.Cell>

        <IndexTable.Cell className={styles.skuColumn}>
            <span className={styles.textEllipsis} title={product.sku}>
                {product.sku}
            </span>
        </IndexTable.Cell>

        <IndexTable.Cell className={styles.variantColumn}>
            <span className={styles.textEllipsis} title={product.variant}>
                {product.variant}
            </span>
        </IndexTable.Cell>

        <IndexTable.Cell className={styles.quantityColumn}>
            <div className={styles.cellRightAligned}>{product.startingQuantity}</div>
        </IndexTable.Cell>

        <IndexTable.Cell className={styles.quantityColumn}>
            <div className={styles.cellRightAligned}>{!product.isCounted ? <Text tone="subdued">Not Counted</Text> : product.countedQuantity}</div>
        </IndexTable.Cell>

        <IndexTable.Cell className={styles.quantityColumn}>
            <div className={styles.discrepancyCell} style={{ backgroundColor: getDiscrepancyBackground(product.discrepancy) }}>
                <Text tone={product.discrepancy !== 0 ? 'critical' : 'success'}>{product.discrepancy > 0 ? `+${product.discrepancy}` : product.discrepancy}</Text>
            </div>
        </IndexTable.Cell>
    </IndexTable.Row>
);

const ProductsListCard = ({ productData, pagination, sortedColumnIndex, sortDirection, onSort, breakpoints, itemsFetching, onPageChange, currentPage }) => {
    const uniqueSkus = pagination?.totalCount || productData.length;
    const startItem = pagination ? (pagination.currentPage - 1) * pagination.pageSize + 1 : 1;
    const endItem = pagination ? Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount) : productData.length;

    return (
        <Card>
            <BlockStack gap="400">
                <InlineStack align="space-between">
                    <Text variant="headingLg">Products List</Text>
                    <Text variant="bodyMd" tone="subdued">
                        {pagination ? `Showing ${startItem}-${endItem} of ${uniqueSkus} unique SKUs` : `${uniqueSkus} unique SKUs`}
                    </Text>
                </InlineStack>
            </BlockStack>

            <BlockStack className={styles.scrollableTableContainer}>
                <IndexTable
                    condensed={breakpoints.smDown}
                    itemCount={productData.length}
                    sortable={[true, true, true, true, true, true]}
                    sortDirection={sortDirection}
                    sortColumnIndex={sortedColumnIndex}
                    onSort={onSort}
                    headings={TABLE_HEADINGS}
                    selectable={false}
                >
                    {productData.map((product) => (
                        <ProductTableRow key={product.id} product={product} />
                    ))}
                </IndexTable>
            </BlockStack>

            {productData.length !== 0 && pagination !== null && (
                <Box padding="400">
                    <InlineStack align="left" gap="200">
                        <Pagination
                            hasPrevious={pagination.hasPrevious && !itemsFetching}
                            hasNext={pagination.hasNext && !itemsFetching}
                            onPrevious={() => onPageChange(currentPage - 1)}
                            onNext={() => onPageChange(currentPage + 1)}
                            label={`${startItem}-${endItem} of ${pagination.totalCount}`}
                        />
                    </InlineStack>
                </Box>
            )}
        </Card>
    );
};

// Custom hooks
const useCycleCountData = (id, sortBy = 'sku', sortDirection = 'ascending') => {
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();

    // Convert Polaris sort direction to API format
    const apiSortDirection = sortDirection === 'descending' ? 'desc' : 'asc';

    const detailsQuery = useQuery({
        queryKey: ['cycleCount', id],
        queryFn: () => cycleCountService.getById(id),
        enabled: !!id,
    });

    const itemsQuery = useQuery({
        queryKey: ['cycleCountItems', id, currentPage, sortBy, sortDirection],
        queryFn: () => cycleCountService.getItems(id, currentPage, ITEMS_PER_PAGE, sortBy, apiSortDirection),
        enabled: !!id,
        placeholderData: (previousData) => previousData,
    });

    if (detailsQuery.data?.data && !detailsQuery.isStale) {
        // check status and redirect to perform page if needed
        if (PERFORM_STATUSES.includes(detailsQuery.data.data.status)) {
            navigate(`/cycle-counts/${id}/perform`, { replace: true });
        }
    }

    return {
        cycleCount: detailsQuery.data?.data,
        items: itemsQuery.data?.data?.items || [],
        pagination: itemsQuery.data?.data?.pagination || null,
        isLoading: detailsQuery.isLoading,
        isError: detailsQuery.isError || itemsQuery.isError,
        isFetching: itemsQuery.isFetching,
        currentPage,
        setCurrentPage,
    };
};

// Reset to page 1 when sort changes
const useResetPageOnSort = (setCurrentPage, sortedColumnIndex, sortDirection) => {
    useEffect(() => {
        setCurrentPage(1);
    }, [sortedColumnIndex, sortDirection, setCurrentPage]);
};

const useCycleCountActions = (id) => {
    const queryClient = useQueryClient();
    const { addToast, clearToasts } = useToast();
    const navigate = useNavigate();

    // is loading state
    const [isApproveLoading, setIsApproveLoading] = useState(false);
    const [isRejectLoading, setIsRejectLoading] = useState(false);
    const [isExportLoading, setIsExportLoading] = useState(false);
    const [isUpdateStatusLoading, setIsUpdateStatusLoading] = useState(false);

    const loadingStates = {
        isApproveLoading,
        isRejectLoading,
        isExportLoading,
        isUpdateStatusLoading,
    };

    // Invalidate relevant queries
    const invalidateQueries = useCallback(() => {
        queryClient.invalidateQueries(['cycleCount', id]);
        queryClient.invalidateQueries(['activityLogs']);
    }, [id, queryClient]);

    const handleApprove = useCallback(async () => {
        try {
            // Show loading state
            setIsApproveLoading(true);
            // Approve cycle count
            const response = await cycleCountService.approve(id);

            // Failures come back as a 200 with success: false, so axios never throws and
            // the catch below would not see them.
            if (!response.success) {
                clearToasts();
                addToast({ message: response.error?.details || response.error?.message || 'Failed to approve cycle count', type: 'error' });

                // Refresh anyway: the status has been reverted server side.
                invalidateQueries();
                setIsApproveLoading(false);
                return;
            }

            // Invalidate queries to refresh data
            invalidateQueries();

            // Hide loading state
            setTimeout(() => {
                setIsApproveLoading(false);
            }, 1000);
        } catch (error) {
            setIsApproveLoading(false);
            clearToasts();
            addToast({ message: error.response?.data?.error?.message || 'Failed to approve cycle count', type: 'error' });
            console.error('Error approving cycle count:', error);
        }
    }, [id, invalidateQueries, addToast, clearToasts]);

    const [isRetrySyncLoading, setIsRetrySyncLoading] = useState(false);

    const handleRetryInventorySync = useCallback(async () => {
        try {
            setIsRetrySyncLoading(true);

            const response = await cycleCountService.retryInventorySync(id);

            clearToasts();

            if (!response.success) {
                addToast({ message: response.error?.details || 'Failed to restart inventory sync', type: 'error' });
            } else {
                addToast({ message: 'Inventory sync restarted', type: 'success' });
            }

            // The push runs in the background, so what lands here is the restart, not its
            // outcome; the banner clears on a later refresh once it finishes.
            invalidateQueries();
        } catch (error) {
            console.error('Error retrying inventory sync:', error);
            clearToasts();
            addToast({ message: error.response?.data?.error?.message || 'Failed to restart inventory sync', type: 'error' });
        } finally {
            setIsRetrySyncLoading(false);
        }
    }, [id, invalidateQueries, addToast, clearToasts]);

    const handleReject = useCallback(
        async (reason) => {
            try {
                // Show loading state
                setIsRejectLoading(true);

                // Reject cycle count
                const response = await cycleCountService.reject(id, reason);

                if (!response.success) {
                    clearToasts();
                    addToast({ message: 'Failed to reject cycle count', type: 'error' });
                    setIsRejectLoading(false);
                    return;
                }

                // Invalidate queries to refresh data
                invalidateQueries();

                // Redirect to perform page if status is IN_PROGRESS
                if (response.data.cycleCount.status === STATUS.IN_PROGRESS) {
                    navigate(`/cycle-counts/${id}/perform`, { replace: true });
                }

                // Hide loading state
                setTimeout(() => {
                    setIsRejectLoading(false);
                }, 1000);
            } catch (error) {
                setIsRejectLoading(false);
                console.error('Error rejecting cycle count:', error);
            }
        },
        [id, invalidateQueries]
    );

    const handleExport = useCallback(async () => {
        try {
            // Show loading state
            setIsExportLoading(true);

            // Show loading toast
            clearToasts();
            addToast({ message: 'Preparing export...', type: 'info' });

            const response = await cycleCountService.export(id);

            // Check if the response is an error (JSON) instead of a CSV blob
            const contentType = response.headers['content-type'];
            if (contentType && contentType.includes('application/json')) {
                // Parse the error response from the blob
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                setIsExportLoading(false);
                clearToasts();
                addToast({
                    message: (
                        <BlockStack gap="100">
                            <Text variant="bodyMd">{errorData.error.message}</Text>
                            <Text variant="bodyMd" tone="subdued">
                                {errorData.error.details}
                            </Text>
                        </BlockStack>
                    ),

                    type: 'error',
                });
                return;
            }

            // Create blob from response
            const blob = new Blob([response.data], { type: 'text/csv' });

            // Extract filename from Content-Disposition header if available
            const contentDisposition = response.headers['content-disposition'];
            let filename = `cycle-count-${id}-${new Date().toISOString().split('T')[0]}.csv`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            // Create download link and trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Hide loading state
            setTimeout(() => {
                setIsExportLoading(false);
            }, 1000);

            // Show success toast
            clearToasts();
            addToast({ message: 'Export downloaded successfully', type: 'success' });
        } catch (error) {
            setIsExportLoading(false);
            console.error('Error exporting cycle count:', error);
            clearToasts();
            addToast({
                message: error.response?.data?.message || 'Failed to export cycle count. Please try again.',
                type: 'error',
            });
        }
    }, [id, addToast, clearToasts]);

    const handleUpdateStatus = useCallback(
        async (status) => {
            try {
                // Show loading state
                setIsUpdateStatusLoading(true);
                // Update cycle count status
                const response = await cycleCountService.updateStatus(id, status);

                // Show error toast if update failed
                if (!response.success) {
                    setIsUpdateStatusLoading(false);
                    clearToasts();
                    addToast({ message: response.error?.message || 'Failed to reopen cycle count', type: 'error' });
                    return;
                }

                // Invalidate queries to refresh data
                invalidateQueries();

                // Hide loading state
                setTimeout(() => {
                    setIsUpdateStatusLoading(false);
                }, 1000);

                // Redirect to perform page if status is IN_PROGRESS
                if (response.data.cycleCount.status === STATUS.IN_PROGRESS) {
                    navigate(`/cycle-counts/${id}/perform`, { replace: true });
                }
            } catch (error) {
                setIsUpdateStatusLoading(false);
                console.error('Error updating cycle count status:', error);
                clearToasts();
                addToast({ message: error.response?.data?.error?.message || 'Failed to reopen cycle count. Please try again.', type: 'error' });
            }
        },
        [id, invalidateQueries, navigate, addToast, clearToasts]
    );

    return { handleApprove, handleReject, handleExport, handleUpdateStatus, handleRetryInventorySync, isRetrySyncLoading, loadingStates };
};

// Main component
const CycleCountDetail = () => {
    const { id } = useParams();
    const breakpoints = useBreakpoints();
    const { currentUser } = useContext(SessionContext);

    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [sortedColumnIndex, setSortedColumnIndex] = useState(4);
    const [sortDirection, setSortDirection] = useState('descending');

    // Map column index to field name
    const sortByField = COLUMN_FIELDS[sortedColumnIndex] || 'countedQuantity';

    const { cycleCount, items, pagination, isLoading, isError, isFetching, currentPage, setCurrentPage } = useCycleCountData(id, sortByField, sortDirection);

    useResetPageOnSort(setCurrentPage, sortedColumnIndex, sortDirection);

    const { handleApprove, handleReject, handleExport, handleUpdateStatus, handleRetryInventorySync, isRetrySyncLoading, loadingStates } = useCycleCountActions(id);

    const handleSort = useCallback((headingIndex, direction) => {
        setSortedColumnIndex(headingIndex);
        setSortDirection(direction);
    }, []);

    const handleRejectWithClose = useCallback(
        async (reason) => {
            await handleReject(reason);
            setRejectModalOpen(false);
        },
        [handleReject]
    );

    if (currentUser && cycleCount && !hasPermissionForLocation(currentUser, cycleCount?.locationId)) {
        return <NoPermissions permission="cycle-count" />;
    }

    // Loading state
    if (isLoading) {
        return (
            <>
                <CycleCountBreadcrumbs cycleCount={null} isLoading={true} />

                <Page fullWidth>
                    <Layout>
                        <Layout.Section>
                            <Card>
                                <Box padding="400" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                    <Spinner size="large" />
                                </Box>
                            </Card>
                        </Layout.Section>
                    </Layout>
                </Page>
            </>
        );
    }

    if (isError) {
        return <NoPermissions permission="cycle-count" />;
    }

    const productData = transformItemsToProductData(items);

    return (
        <>
            <CycleCountBreadcrumbs cycleCount={cycleCount} isLoading={isLoading} />

            <Page fullWidth>
                <Layout>
                    {cycleCount?.inventorySyncError && (
                        <Layout.Section>
                            <Banner title="Inventory was not fully updated in Shopify" tone="warning" action={{ content: 'Retry', onAction: handleRetryInventorySync, loading: isRetrySyncLoading }}>
                                <Text as="p">{cycleCount.inventorySyncError}</Text>
                                <Text as="p" tone="subdued">
                                    The approval stands. Retrying sends only the quantities that have not reached Shopify yet.
                                </Text>
                            </Banner>
                        </Layout.Section>
                    )}

                    <Layout.Section>
                        <CycleCountDetails
                            cycleCount={cycleCount}
                            onExport={handleExport}
                            onApprove={handleApprove}
                            onUpdateStatus={handleUpdateStatus}
                            loadingStates={loadingStates}
                            onReject={() => setRejectModalOpen(true)}
                        />
                    </Layout.Section>

                    <Layout.Section>
                        <ProductsListCard
                            productData={productData}
                            pagination={pagination}
                            sortedColumnIndex={sortedColumnIndex}
                            sortDirection={sortDirection}
                            onSort={handleSort}
                            breakpoints={breakpoints}
                            itemsFetching={isFetching}
                            onPageChange={setCurrentPage}
                            currentPage={currentPage}
                        />
                    </Layout.Section>

                    <Layout.Section>
                        <Card>
                            <ActivityLog objectType="CycleCount" objectId={id} />
                        </Card>
                    </Layout.Section>
                </Layout>
            </Page>

            <RejectModal active={rejectModalOpen} onClose={() => setRejectModalOpen(false)} onReject={handleRejectWithClose} />
        </>
    );
};

export default CycleCountDetail;
